import { useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';

import { useLayoutGoalReport } from '@queries';
import { activeUserIdAtom, layoutAtom } from '@store';
import { Box, Spinner, Stack, StatusBox, Tabs, u } from '@ui';

import { GoalReportCharts } from './components/GoalReportCharts';
import { GoalReportControls, GoalReportControlsProps } from './components/GoalReportControls';
import { GoalReportFilters } from './components/GoalReportFilters/GoalReportFilters';
import { GoalReportHeadingGroup } from './components/GoalReportHeadingGroup';
import { GoalReportSwings } from './components/GoalReportSwings';
import * as css from './GoalReport.css';
import { GoalReportProvider } from './GoalReportContext';
import { getReportPeriodDates } from './goalReportUtils';

type GoalReportWrapperProps = {
    backButton?: React.ReactNode;
    children: React.ReactNode;
};

const GoalReportWrapper = ({ children, backButton }: GoalReportWrapperProps) => (
    <Box
        display="flex"
        position="absolute"
        top={0}
        width="100%"
        p={u(2.5)}
        flexDirection="column"
        height="100%"
        className={css.goalReportPage}
    >
        <GoalReportControls backButton={backButton} />
        <Stack flex={1} gap={0} p={u(2.5)} className={css.contentWrapper}>
            {children}
        </Stack>
    </Box>
);

type GoalReportProps = GoalReportControlsProps & {
    getFullActivityRoute: (activity: number, swingUuid: string) => string;
};

export const GoalReport = ({ backButton, getFullActivityRoute }: GoalReportProps) => {
    const layout = useAtomValue(layoutAtom);
    const hasValidLayoutId = typeof layout?.id === 'number' && Number.isInteger(layout.id);

    if (!hasValidLayoutId) {
        return (
            <GoalReportWrapper backButton={backButton}>
                <GoalReportError layoutId={layout?.id} />
            </GoalReportWrapper>
        );
    }

    return (
        <GoalReportWrapper backButton={backButton}>
            <MainGoalReport getFullActivityRoute={getFullActivityRoute} layoutId={layout.id} />
        </GoalReportWrapper>
    );
};

type MainGoalReportProps = { layoutId: number } & Pick<GoalReportProps, 'getFullActivityRoute'>;

const MainGoalReport = ({ layoutId, getFullActivityRoute }: MainGoalReportProps) => {
    const activeUserId = useAtomValue(activeUserIdAtom);
    const { data: totalGoalReport, isLoading, error } = useLayoutGoalReport({ layoutId });
    const swings = totalGoalReport?.[0].swings;
    const period = useMemo(() => getReportPeriodDates(swings), [swings]);

    if (isLoading && !error) {
        return (
            <Stack height="100%" alignItems="center" justifyContent="center">
                <Spinner size="l" />
            </Stack>
        );
    }

    if (error) {
        return <GoalReportError layoutId={layoutId} />;
    }

    return (
        <GoalReportProvider
            layoutId={Number(layoutId)}
            activeUserId={activeUserId}
            reportPeriod={period}
            getFullActivityRoute={getFullActivityRoute}
        >
            <GoalReportHeadingGroup />
            <GoalReportFilters />
            <ReportRenderer />
        </GoalReportProvider>
    );
};

const tabs = [
    { label: 'Report', id: 'report' },
    { label: 'Manage Swings', id: 'manage' },
];

const ReportRenderer = () => {
    const [activeItem, setActiveItem] = useState(tabs[0].id);
    return (
        <>
            <Box mb={u(2)}>
                <Tabs activeId={activeItem} items={tabs} onClick={(newItem) => setActiveItem(newItem)} />
            </Box>
            <Box flex={1} style={{ overflow: 'auto' }}>
                {activeItem === tabs[0].id && <GoalReportCharts />}
                {activeItem === tabs[1].id && <GoalReportSwings />}
            </Box>
        </>
    );
};

const GoalReportError = ({ layoutId }: { layoutId: number | undefined }) => {
    const msg = layoutId
        ? `The goal report was not found for layout id:${layoutId}`
        : `Could not generate goal report, no layout id provided`;
    return <StatusBox display="flex" width="fit-content" status="info" title="No Report" description={msg} />;
};
